
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Action, Getter } from 's-vuex-class'
import {
    SuggestionAction,
    SuggestionLinkMode,
    SuggestionView,
} from '../../entities/suggestions'
import DialogBody from './DialogBody.vue'
import { UserDetail } from '@/entities/user'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import AvatarOrInitials from '@/components/WebView/AvatarOrInitials.vue'
import { ComponentMounter } from '@/utils/component-mounter'

@Component<Dialog>({
    components: { DialogBody, AvatarOrInitials },
})
export default class Dialog extends Vue {
    @Prop(Object) public suggestion!: SuggestionView
    @Prop({ type: String, default: '' }) public title!: string
    @Prop({ type: String, default: '' }) public subtitle!: string
    @Prop({ type: String, default: '' }) public body!: string
    @Prop({
        type: Object,
        default() {
            return { id: 0, name: '', avatar: '' }
        },
    })
    public user!: UserDetail
    @Prop({ type: Number, default: SuggestionLinkMode.None })
    public linkMode!: SuggestionLinkMode
    @Prop({ type: Number, default: 0 }) public created!: number
    @Prop({ type: String, default: '' }) public action!: SuggestionAction
    @Prop({ type: String, default: '' }) public type!: string
    @Prop({ type: Boolean, default: false }) public alert!: boolean
    @Prop({ type: Boolean, default: false }) public withVerticalLine!: boolean
    @Prop({ type: Boolean, default: false }) public showDelete!: boolean

    @Getter public activeSuggestionId
    @Getter public getUserId!: number
    @Getter public getUserIsAdmin!: boolean

    @Action public removeSuggestion
    @Action public cleanActiveSuggestion
    @Action public setActiveSuggestionId

    public get canDelete() {
        return (
            this.showDelete &&
            this.suggestion &&
            (this.suggestion.user_id === this.getUserId || this.getUserIsAdmin)
        )
    }

    public get suggestionLink() {
        if (this.linkMode === SuggestionLinkMode.None) {
            return undefined
        }

        if (this.linkMode === SuggestionLinkMode.Path) {
            return {
                name: 'suggestionsDetail',
                params: { id: this.suggestion.id.toString() },
            }
        }

        return { query: { suggestion: this.suggestion.id } }
    }

    // where recent is 'in the last minute'
    public get recentlyCreated() {
        const now = new Date().valueOf()
        const then = this.created * 1000
        return now - then < 1000 * 60
    }

    public get getVerticalLineClass() {
        if (!this.withVerticalLine) {
            return 'none'
        }
        if (!this.action) {
            return '' // non-action display as gray by default
        }
        return this.action === 'not-now' ? 'negative' : 'positive'
    }

    public askDelete() {
        const container = this.$refs.container as Element
        const propsData = {
            title: 'Are You Sure?',
            message: 'You are about to delete this suggestion',
            okCaption: 'Delete',
            visible: true,
        }
        const dialogInstance = ComponentMounter.mount(
            ConfirmDialog,
            container,
            propsData,
            this.$store
        )

        dialogInstance.$once('ok', () => {
            if (this.removeSuggestion(this.suggestion)) {
                if (this.suggestion.id === this.activeSuggestionId) {
                    this.setActiveSuggestionId(0)
                    this.cleanActiveSuggestion()
                }
            }

            dialogInstance.$destroy()
        })

        dialogInstance.$once('cancel', () => {
            dialogInstance.$destroy()
        })
    }
}
